import React, { useState } from 'react'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Grid from 'components_new/atoms/Grid'

import SettingsEditItem from 'components_new/molecules/SettingsItem/SettingsEditItem'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'
import SettingsPopup from 'components_new/molecules/SettingsPopup'
import SettingsToggleOption from 'components_new/molecules/SettingsToggleOption'

import {
  DEFAULT_STATE,
  useTagOptions,
  isLoading,
  ITagStatusData,
  translateSeverity,
  translateStatusCondition,
  translateStatusDateValue,
  translateStatusValueLabel
} from './utils'

import {
  TagStatusCondition,
  TagStatusDateValue,
  TagStatusSeverity,
  WidgetTagObject,
  WidgetTagStatusPostAttributes
} from 'types/GlobalWidgetTag'
import { DatasetType } from 'types/GlobalWidget'
import { useTagOptionsFetched } from 'redux/hooks/TagOptions'

interface AddProps {
  createWidgetTagStatus: (
    body: WidgetTagStatusPostAttributes,
    tagId: string
  ) => void
  onCancel: () => void
  onClose: () => void
  open: boolean
  tag: WidgetTagObject
}

const Add = (props: AddProps) => {
  const {
    createWidgetTagStatus,
    onCancel,
    onClose,
    open,
    tag
  } = props

  const tagOptionsFetched = useTagOptionsFetched()

  const [data, setData] = useState<ITagStatusData>(DEFAULT_STATE)
  const { t } = useTranslation()

  const handleCancel = () => {
    onCancel()
    setData(DEFAULT_STATE)
  }

  const handleUpdate = (body: Partial<ITagStatusData>) => {
    const updateBody = {
      ...body
    }

    if (
      body.value === TagStatusDateValue.DAYS_FROM_NOW ||
      body.value === TagStatusDateValue.DAYS_SINCE_NOW
    ) {
      // set default conditional value
      updateBody.conditional_value = data.conditional_value ?? 0
    }

    if (
      body.value === TagStatusDateValue.TODAY ||
      body.value === TagStatusDateValue.TOMORROW ||
      body.value === TagStatusDateValue.YESTERDAY
    ) {
      // remove conditional value
      updateBody.conditional_value = null
    }

    // default
    setData((prevState) => ({
      ...prevState,
      ...body
    }))
  }

  const handleSubmit = () => {
    createWidgetTagStatus(
      {
        data: {
          ...data,
          value: data.value as string,
          widget_tag_id: tag.id
        }
      },
      tag.id
    )

    handleCancel()
  }

  const disabled =
    data.value === null ||
    ((data.value === TagStatusDateValue.DAYS_FROM_NOW ||
      data.value === TagStatusDateValue.DAYS_SINCE_NOW) &&
      data.conditional_value === null)

  const options = useTagOptions(tag)
  const loading = isLoading(tag, tagOptionsFetched)

  return (
    <>
      <SettingsPopup
        disabled={disabled}
        disabledTitle={t('widgets', 'select_value_to_add')}
        onCancel={handleCancel}
        onClose={onClose}
        onSubmit={handleSubmit}
        open={open}
        title={t('widgets', 'new_indication')}
      >
        <>
          <SettingsGroup helperText={t('widgets', 'set_condition_helper_text')}>
            {/*-- condition --*/}
            <SettingsEditItem<TagStatusCondition>
              input={{
                options: tag.settings.status.condition_options.map((option) => {
                  return {
                    title: translateStatusCondition[option][tag.type],
                    id: option
                  }
                }),
                type: 'select',
                value: data.condition
              }}
              onSubmit={(value) => handleUpdate({ condition: value })}
              title={translateStatusValueLabel[tag.type]}
              renderValue={(value) => translateStatusCondition[value][tag.type]}
            />

            {/*-- additional value --*/}
            {tag.type === DatasetType.DATE &&
            (data.value === TagStatusDateValue.DAYS_FROM_NOW ||
              data.value === TagStatusDateValue.DAYS_SINCE_NOW) ? (
                  <SettingsEditItem<number | null>
                    input={{
                      type: 'number',
                      value: data.conditional_value
                    }}
                    onSubmit={(value) => {
                      handleUpdate({ conditional_value: value })
                    }}
                    unit={t('widgets', 'days')}
                  />
                ) : null}

            {/*-- value --*/}
            <SettingsEditItem<number | string | TagStatusDateValue | null>
              input={{
                options: options.map((option) => ({
                  title:
                    tag.type === DatasetType.DATE
                      ? translateStatusDateValue[option as TagStatusDateValue]
                      : option,
                  id: option
                })),
                type:
                  tag.type === DatasetType.DATE || tag.type === DatasetType.ENUM
                    ? 'select'
                    : 'text',
                value: data.value
              }}
              loading={loading}
              onSubmit={(value) => handleUpdate({ value: value })}
              renderValue={(value) => {
                if (tag.type === DatasetType.DATE) {
                  return translateStatusDateValue[value as TagStatusDateValue]
                } else {
                  return value?.toString() ?? ''
                }
              }}
            />
          </SettingsGroup>

          {/*-- severity --*/}
          <SettingsGroup helperText={t('widgets', 'select_indicator_helper_text')}>
            <SettingsItem>
              <Grid container={true} columns={3}>
                {Object.values(TagStatusSeverity).map((severity) => {
                  const selected = severity === data.severity

                  return (
                    <Grid item={true} key={severity} xs={1}>
                      <SettingsToggleOption
                        onClick={() => handleUpdate({ severity: severity })}
                        selected={selected}
                        title={translateSeverity[severity]}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 16,
                            bgcolor: `${severity}.main`,
                            border: '1px solid',
                            borderColor: 'divider',
                            opacity: selected ? 1 : 0.5,
                            transition: 'all 240ms ease 0s',
                            '&:hover': {
                              borderColor: 'action.focus'
                            }
                          }}
                        />
                      </SettingsToggleOption>
                    </Grid>
                  )
                })}
              </Grid>
            </SettingsItem>
          </SettingsGroup>
        </>
      </SettingsPopup>
    </>
  )
}

export default Add
